<template>
  <EStickyFooterLayout @throttled-scroll="onWindowScroll">
    <template #header>
      <EMainHeader
        :is-sticky="stickyHeader"
        :has-scroll-transition="scrollUpHeader"
        :minimal="minimaliseHeader && !isAtTop"
        :track-search-bar-click-func="trackSearchBarClick"
        personal-counter-increment
        personal-counter-impact-redirect
        :search-hidden-inputs="searchHiddenInputs"
        data-test-id="layout-header"
      >
        <template #navigation>
          <ENavigation
            :shopping-link="hasShoppingLink"
            :track-vertical-click-func="trackNavigationVerticalClick"
            data-test-id="layout-navigation"
          />
        </template>
      </EMainHeader>
    </template>
    <template v-if="filtersShown" #filters>
      <EFilters :filters="filters" />
    </template>
    <template #content>
      <Nuxt data-test-id="layout-content" />
    </template>
    <template #footer>
      <EMainFooter data-test-id="layout-footer" />
    </template>
  </EStickyFooterLayout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import screen from '~~/common/vue/mixins/screen';
import EStickyFooterLayout from '~~/common/vue/components/layouts/sticky-footer';
import EMainHeader from '~~/common/vue/components/main-header';
import EMainFooter from '~~/common/vue/components/main-footer';
import ENavigation from '~~/search/common/vue/components/navigation';
import EFilters from '~~/search/common/vue/components/filters';
import { trackSearchBarClick, trackVerticalClick } from '~~/search/common/js/client/analytics/header';
import filters from '~/filters';

import { stickyHeaderExperiment } from '~~/common/js/constants/feature-flags';

export default {
  head() {
    const meta = [
      { hid: 'description', name: 'description', content: this.$t('common.header.share.subheader') },
      { property: 'og:title', name: 'og:title', content: this.$t('common.header.share') },
      { property: 'og:description', name: 'og:description', content: this.$t('common.header.share.subheader') },
      { property: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    ];
    if (this.$route.query.cpr) {
      // Stripping any extra info like typetags or addon query param
      const cprUrl = `${process.env.baseUrl}/search?q=${this.$route.query.q}&cpr=${this.$route.query.cpr}`;
      meta.push(
        { hid: 'og-image', property: 'og:image', name: 'og:image', content: `${process.env.cdnUrl}/static/og/cpr_amazon.png` },
        { hid: 'og-url', property: 'og:url', name: 'og:url', content: cprUrl },
      );
    }
    return {
      htmlAttrs: { lang: this.$store.state.global.locale },
      title: `${this.$route.query.q?.toString() || ''} - Ecosia`,
      meta,
    };
  },
  components: {
    EStickyFooterLayout,
    EFilters,
    ENavigation,
    EMainHeader,
    EMainFooter,
  },
  mixins: [screen],
  data() {
    return {
      stickyHeaderExperiment: null,
      isAtTop: true,
      isScrollingUp: false,
      scrollY: 0,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('search', ['filtersShown']),
    ...mapState('user', ['appUserSnowplowId', 'isEcosiaApp']),
    ...mapGetters(['hasShoppingLink']),
    ...mapGetters('unleash', ['getFeatureToggle']),
    searchHiddenInputs() {
      // The reason this is an unfriendly '_sp' is because this is a
      // built in feature of snowplow that we are using. So it has to be.
      // https://snowplowanalytics.com/blog/2015/03/15/snowplow-javascript-tracker-2.4.0-released/#cross-domain
      // eslint-disable-next-line no-underscore-dangle
      return (this.isEcosiaApp) ? { _sp: this.appUserSnowplowId } : {};
    },
    filters() {
      // enrich the static filter config with translated strings
      return filters.map((config) => {
        config.hint = this.$t(`web.filters.${config.id}.hint`);
        config.placeholder = this.$t(`web.filters.${config.id}.placeholder`);
        config.options = config.options.map((option) => {
          option.label = this.$t(`web.filters.${config.id}.options.${option.value}`);
          return option;
        });
        return config;
      });
    },
    stickyHeader() {
      return this.isDesktopUp &&
        (this.completelyStickyHeader ||
        this.minimaliseHeader ||
        (this.scrollUpHeader && this.isScrollingUp));
    },
    completelyStickyHeader() {
      return this.isDesktopUp &&
             this.stickyHeaderExperiment?.value === stickyHeaderExperiment.completelySticky;
    },
    minimaliseHeader() {
      return this.isDesktopUp &&
             this.stickyHeaderExperiment?.value === stickyHeaderExperiment.minimalise;
    },
    scrollUpHeader() {
      return this.isDesktopUp &&
             this.stickyHeaderExperiment?.value === stickyHeaderExperiment.scrollUp;
    },
  },
  created() {
    this.stickyHeaderExperiment = this.getFeatureToggle(stickyHeaderExperiment.flag);
  },
  beforeMount() {
    this.isAtTop = window?.scrollY <= 0;
  },
  methods: {
    onWindowScroll(scrollState) {
      this.isScrollingUp = scrollState?.scrollingUp || false;
      this.isAtTop = scrollState?.atTop;
      this.scrollY = scrollState?.y || 0;
    },
    trackNavigationVerticalClick(verticalId) {
      this.$track(trackVerticalClick(verticalId, this.scrollY));
    },
    trackSearchBarClick() {
      this.$track(trackSearchBarClick(this.scrollY));
    },
  },
};
</script>
